/**
 * Contact store
 *
 * State and actions for contacts. Holds the list (with addresses), all fetched from
 * GET /api/contacts (paginated), and CRUD via the contact service. Use fetchContacts({ mode }) to
 * load/reload the list (e.g. when the form loads or after create/edit/delete).
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Contact, ContactCreateBody, ContactPatchBody } from '~~/layers/base/types/contact'
import { getApiErrorMessage } from '~~/layers/base/utils/getApiErrorMessage'
import { buildContactAddressPatchBody } from '~~/layers/base/utils/crane/address'
import { useContactService } from '@/services/contact.service'

/** Address row from GET /api/contacts (snake_case). */
export interface RecipientContactAddress {
    id: string
    label?: string | null
    prefix?: string | null
    suffix?: string | null
    first_name?: string | null
    middle_name?: string | null
    last_name?: string | null
    nickname?: string | null
    raw_address_str?: string | null
    street_address_1?: string | null
    street_address_2?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
    country?: string | null
    country_code?: string | null
}

/** Contact with addresses from GET /api/contacts. */
export interface RecipientContact {
    id: string
    name: string
    phone?: string | null
    email?: string | null
    created_at: string
    updated_at: string
    profile_id: string
    addresses: RecipientContactAddress[]
}

/** Response from GET /api/contacts/[id] (contact with full address rows for edit form). */
export interface GetContactResponse {
    contact: {
        id: string
        name: string
        phone?: string | null
        email?: string | null
        created_at: string
        updated_at: string
        profile_id: string
    }
    addresses: ContactAddress[]
}

/** Full address row (for edit form); matches server address table shape. */
export interface ContactAddress extends RecipientContactAddress {
    label?: string | null
    prefix?: string | null
    suffix?: string | null
    first_name?: string | null
    middle_name?: string | null
    last_name?: string | null
    nickname?: string | null
    is_default?: boolean
}

/** Payload for POST/PATCH address (matches server `validateAddress` shape). */
export interface AddressPayload {
    raw_address_str?: string | null
    label?: string | null
    prefix?: string | null
    suffix?: string | null
    first_name?: string | null
    middle_name?: string | null
    last_name?: string | null
    nickname?: string | null
    street_address_1?: string | null
    street_address_2?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
    country?: string | null
    /** ISO country code when present; create/update also derive this from `country`. */
    country_code?: string | null
}

/**
 * Sort key sent to GET /api/contacts. Known values are suggested for autocomplete, but any string
 * is accepted so new server-side sorts can be used without changing this type — the server
 * whitelists/maps the value (unknown → default 'name').
 */
export type ContactsSort = 'name' | 'created_at' | (string & {})

/** Wire params for a single contacts fetch (what the service forwards as the query string). */
export interface FetchContactsOptions {
    search?: string
    sort?: ContactsSort
    /** 1-based page number (default 1 server-side). */
    page?: number
    /** Contacts per page (default/clamped to 100 server-side). */
    pageSize?: number
    /**
     * Restrict `search` to Contact fields only, skipping Address fields (server default: false,
     * i.e. search matches Contact and all of its Addresses). Optimization for contexts like a
     * contact auto-complete picker where address matches aren't useful.
     */
    searchExcludeAddresses?: boolean
}

/**
 * How fetchContacts() applies the result to store state:
 *  - 'page'   → fetch `page` (default 1) and REPLACE the list (initial load / search / page jump).
 *  - 'append' → fetch the page after the current one and APPEND it (infinite scroll / "load more").
 *  - 'all'    → loop through every page and replace the list with the complete set (recipient
 *               pickers that need the whole address book in memory); clears pagination state.
 */
export type FetchContactsMode = 'page' | 'append' | 'all'

/** Params for the unified fetchContacts() store action. */
export interface FetchContactsParams extends FetchContactsOptions {
    mode?: FetchContactsMode
}

/** Pagination metadata returned by GET /api/contacts. */
export interface ContactsPagination {
    /** Current page number (1-based). */
    page: number
    /** Contacts per page. */
    pageSize: number
    /** Total matching contacts across all pages. */
    totalRecords: number
    /** Total number of pages. */
    totalPages: number
    /** Number of contacts in this page. */
    contactCount: number
    /** Number of addresses across this page's contacts (current page only). */
    addressCount: number
    /** Total addresses across ALL matching contacts (every page). */
    totalAddresses: number
    /** Whether more pages remain after this one. */
    hasMore: boolean
}

/** Response shape from GET /api/contacts. */
export interface FetchContactsResponse {
    contacts: RecipientContact[]
    pagination: ContactsPagination
}

/**
 * One entry in the "Recent Contacts" widget (Home page + Contacts page): a contact that was
 * recently created, or an existing contact who was recently given a new address — whichever is
 * newer. From `GET /api/contacts?sort=recent`, newest first.
 */
export interface RecentContact {
    id: string
    name: string
}

/**
 * Edit contact drawer: PATCH name and PATCH one address per submit (contacts may still
 * have multiple addresses; this flow edits a single row).
 */
export interface SubmitEditSingleAddressPayload {
    name: string
    address: AddressPayload & { id: string; is_default?: boolean }
}

export const useContactStore = defineStore('contact', () => {
    const {
        doFetchContactList,
        doCreateContact,
        doCreateContactWithAddress,
        doFetchContactById,
        doUpdateContact,
        doArchiveContact,
        doPatchAddress,
        doPatchContactName,
    } = useContactService()

    /** List from GET /api/contacts (with addresses). */
    const contacts = ref<RecipientContact[]>([])
    /** Pagination metadata from the last 'page'/'append' fetch; null after a 'all' fetch. */
    const pagination = ref<ContactsPagination | null>(null)
    const currentContact = ref<Contact | null>(null)
    const loading = ref(false)
    /** Addresses for `currentContact`, kept in sync by `getById`; backs `ensureContactDetailLoaded`. */
    const contactDetailAddresses = ref<ContactAddress[]>([])
    const contactDetailStatus = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
    /**
     * Set when the last `fetchList()` failed; cleared on the next successful call. Most call
     * sites fire-and-forget this (e.g. priming the Send-flow recipient picker) and can ignore it,
     * but a page whose whole job is showing the list (contacts/index.vue) should surface it
     * instead of silently rendering an empty list that looks the same as "no contacts yet."
     */
    const lastFetchError = ref('')

    /**
     * Single entry point for loading contacts from GET /api/contacts. The `mode` decides how the
     * result is applied to state (see FetchContactsMode); `sort`/`search`/`pageSize` are forwarded
     * to the server as-is, so new sorts just need a value here — no code change.
     *
     *  - mode 'page'   (default): fetch `page` (default 1), replace the list + pagination state.
     *  - mode 'append'          : fetch the page after the current one and append it (load more);
     *                             no-op once the server reports no more pages.
     *  - mode 'all'             : page through everything and replace the list; clears pagination
     *                             (recipient pickers that need the whole address book in memory).
     *
     * @returns The contacts currently held in state after the fetch.
     */
    async function fetchContacts(params: FetchContactsParams = {}): Promise<RecipientContact[]> {
        const { mode = 'page', search, sort, pageSize } = params
        loading.value = true
        try {
            if (mode === 'all') {
                const all: RecipientContact[] = []
                let page = 1
                // Bounded by the server's hasMore; the outer cap is a safety net against a bad response.
                for (let guard = 0; guard < 1000; guard++) {
                    const { data, error: err } = await doFetchContactList({
                        search,
                        sort,
                        page,
                        pageSize,
                    })
                    if (err.value) {
                        throw err.value
                    }
                    all.push(...(data.value?.contacts ?? []))
                    if (!data.value?.pagination?.hasMore) break
                    page += 1
                }
                contacts.value = all
                pagination.value = null
                lastFetchError.value = ''
                return contacts.value
            }

            // 'append' continues after the current page; 'page' uses the requested page (default 1).
            if (mode === 'append' && pagination.value && !pagination.value.hasMore) {
                return contacts.value
            }
            const page = mode === 'append' ? (pagination.value?.page ?? 0) + 1 : (params.page ?? 1)

            const { data, error: err } = await doFetchContactList({
                search,
                sort,
                page,
                pageSize,
            })
            if (err.value) {
                throw err.value
            }
            const fetched = data.value?.contacts ?? []
            contacts.value = mode === 'append' ? [...contacts.value, ...fetched] : fetched
            pagination.value = data.value?.pagination ?? null
            lastFetchError.value = ''
            return contacts.value
        } catch (err) {
            lastFetchError.value = getApiErrorMessage(err, 'Failed to load contacts.')
            return contacts.value
        } finally {
            loading.value = false
        }
    }

    /**
     * Look up contacts by name for the New Contact "from your contacts" auto-suggest. Restricts
     * search to Contact fields only (search_exclude_addresses — address matches aren't useful
     * for this picker) and does not touch the shared `contacts`/`pagination` list state.
     */
    async function suggestContacts(search: string, limit = 5): Promise<RecipientContact[]> {
        const { data, error: err } = await doFetchContactList({
            search,
            sort: 'name',
            page: 1,
            pageSize: limit,
            searchExcludeAddresses: true,
        })
        if (err.value) {
            throw err.value
        }
        return data.value?.contacts ?? []
    }

    /**
     * Choose Address / Contact Manager list search: same GET /api/contacts as
     * {@link suggestContacts}, but with default search (contact OR address fields).
     * Does not mutate `contacts` / `pagination` — callers keep the full book for empty query.
     */
    async function searchContacts(search: string, limit = 50): Promise<RecipientContact[]> {
        const { data, error: err } = await doFetchContactList({
            search,
            sort: 'name',
            page: 1,
            pageSize: limit,
        })
        if (err.value) {
            throw err.value
        }
        return data.value?.contacts ?? []
    }

    /** Drop contact list cache after logout so the next profile cannot see prior recipients. */
    function clearSessionContacts(): void {
        contacts.value = []
        pagination.value = null
        currentContact.value = null
        lastFetchError.value = ''
    }

    /**
     * Re-load the list after a mutation, preserving the current view mode: if the list was loaded
     * in full (mode 'all', pagination === null) reload it in full; otherwise reset to page 1 of the
     * paginated view. Keeps recipient pickers from silently truncating to one page after an edit.
     */
    async function refreshList(): Promise<void> {
        if (pagination.value == null) {
            await fetchContacts({ mode: 'all' })
        } else {
            await fetchContacts({ mode: 'page', page: 1, pageSize: pagination.value.pageSize })
        }
    }

    /**
     * Create a contact. Refreshes the list on success.
     * @returns The created contact
     */
    async function create(body: ContactCreateBody): Promise<Contact> {
        loading.value = true
        try {
            const { data, error: err } = await doCreateContact(body)
            if (err.value) {
                throw err.value
            }
            const created = data.value!
            await refreshList()
            return created
        } finally {
            loading.value = false
        }
    }

    /**
     * Create a contact with its first address in one call (New Contact page). Refreshes the list
     * on success.
     * @returns { contact, address } as created
     */
    async function createWithAddress(body: {
        name: string
        phone?: string | null
        email?: string | null
        address: AddressPayload
    }): Promise<{ contact: { id: string }; address: { id: string } | null }> {
        loading.value = true
        try {
            const { data, error: err } = await doCreateContactWithAddress(body)
            if (err.value) {
                throw err.value
            }
            const created = data.value!
            await refreshList()
            return created
        } finally {
            loading.value = false
        }
    }

    /**
     * Fetch one contact by id with all addresses (for edit form). Sets currentContact to the contact row.
     * @returns { contact, addresses } for edit form pre-fill
     */
    async function getById(id: string): Promise<GetContactResponse> {
        loading.value = true
        try {
            const { data, error: err } = await doFetchContactById(id)
            if (err.value) {
                contactDetailStatus.value = 'error'
                throw err.value
            }
            const result = data.value!
            currentContact.value = result.contact as unknown as Contact
            contactDetailAddresses.value = result.addresses
            contactDetailStatus.value = 'ready'
            return result
        } finally {
            loading.value = false
        }
    }

    /** Cache-first fetch (mirrors profile's `ensureAddressesLoaded`): instant if cached, revalidates via `getById` in the background. */
    async function ensureContactDetailLoaded(id: string): Promise<GetContactResponse> {
        if (contactDetailStatus.value === 'ready' && currentContact.value?.id === id) {
            const cached: GetContactResponse = {
                contact: currentContact.value as unknown as GetContactResponse['contact'],
                addresses: contactDetailAddresses.value,
            }
            void getById(id).catch(() => {})
            return cached
        }
        return getById(id)
    }

    /** Syncs the cache after an optimistic update (e.g. make-default) that skips `getById`. */
    function setContactDetailAddresses(rows: ContactAddress[]): void {
        contactDetailAddresses.value = rows
    }

    /**
     * Sets one address as default for a contact, same as profile's `setDefaultAddress`.
     * Updates the cache first, then saves it; undoes the change and throws if the save fails.
     */
    async function setDefaultAddress(
        contactId: string,
        addressId: string
    ): Promise<ContactAddress[]> {
        const previous = contactDetailAddresses.value
        if (previous.find((row) => row.id === addressId)?.is_default) {
            return previous
        }
        contactDetailAddresses.value = previous.map((row) => ({
            ...row,
            is_default: row.id === addressId,
        }))
        const { error: err } = await doPatchAddress(contactId, addressId, { is_default: true })
        if (err.value) {
            contactDetailAddresses.value = previous
            throw err.value
        }
        return contactDetailAddresses.value
    }

    /**
     * Update a contact by id. Refreshes the list and currentContact on success.
     * @returns The updated contact
     */
    async function update(id: string, body: ContactPatchBody): Promise<Contact> {
        loading.value = true
        try {
            const { data, error: err } = await doUpdateContact(id, body)
            if (err.value) {
                throw err.value
            }
            const updated = data.value!
            await refreshList()
            if (currentContact.value?.id === id) currentContact.value = updated
            return updated
        } finally {
            loading.value = false
        }
    }

    /**
     * Archive (soft-delete) a contact by id. Hides it from list/detail; notes already sent stay on
     * record. Refreshes the list on success; clears currentContact if it was selected.
     */
    async function remove(id: string): Promise<void> {
        loading.value = true
        try {
            const { error: err } = await doArchiveContact(id)
            if (err.value) {
                throw err.value
            }
            if (currentContact.value?.id === id) currentContact.value = null
            await refreshList()
        } finally {
            loading.value = false
        }
    }

    /**
     * Fetch one contact by id (with addresses) and upsert into `contacts`.
     * Use after edits, or when a page needs a contact that may be missing from a partial list
     * (note status / success refresh fallback).
     * @returns The contact as stored, or null if the fetch failed
     */
    async function refreshContact(id: string): Promise<RecipientContact | null> {
        const trimmed = id.trim()
        if (!trimmed) return null

        const { data, error: err } = await doFetchContactById(trimmed)
        if (err.value || !data.value) return null

        const updated: RecipientContact = {
            ...data.value.contact,
            addresses: data.value.addresses,
        }
        const idx = contacts.value.findIndex((c) => c.id === trimmed)
        if (idx >= 0) {
            contacts.value = [
                ...contacts.value.slice(0, idx),
                updated,
                ...contacts.value.slice(idx + 1),
            ]
        } else {
            contacts.value = [...contacts.value, updated]
        }
        if (currentContact.value?.id === trimmed) {
            currentContact.value = updated as unknown as Contact
        }
        return updated
    }

    /** Clear currentContact (e.g. when leaving a detail view). */
    function clearCurrentContact() {
        currentContact.value = null
    }

    /**
     * Apply edit-drawer changes: PATCH contact name when it changed, PATCH the edited address,
     * then merge the address PATCH response into the in-memory list (no follow-up GET).
     */
    async function submitEditContact(
        contactId: string,
        payload: SubmitEditSingleAddressPayload,
        options: { rawAddressIds?: Set<string>; previousName?: string } = {}
    ): Promise<void> {
        const { name: newName, address: addr } = payload
        const rawAddressIds = options.rawAddressIds ?? new Set<string>()
        const nameTrimmed = newName.trim()
        const previousNameTrimmed = options.previousName?.trim() ?? nameTrimmed

        if (nameTrimmed !== previousNameTrimmed) {
            const { error: nameErr } = await doPatchContactName(contactId, nameTrimmed)
            if (nameErr.value) throw nameErr.value
        }

        const { data, error: patchErr } = await doPatchAddress(
            contactId,
            addr.id,
            buildContactAddressPatchBody(addr, rawAddressIds)
        )
        if (patchErr.value) throw patchErr.value

        const updatedAddress = data.value?.address as RecipientContactAddress | undefined
        if (updatedAddress) {
            applyContactAddressPatchToList(
                contactId,
                updatedAddress,
                nameTrimmed !== previousNameTrimmed ? nameTrimmed : undefined
            )
            return
        }

        await refreshContact(contactId)
    }

    function applyContactAddressPatchToList(
        contactId: string,
        updatedAddress: RecipientContactAddress,
        updatedName?: string
    ): void {
        const idx = contacts.value.findIndex((c) => c.id === contactId)
        if (idx < 0) return

        const existing = contacts.value[idx]!
        const addresses = existing.addresses.map((row) =>
            row.id === updatedAddress.id ? { ...row, ...updatedAddress } : row
        )
        const next: RecipientContact = {
            ...existing,
            name: updatedName ?? existing.name,
            addresses,
        }

        contacts.value = [...contacts.value.slice(0, idx), next, ...contacts.value.slice(idx + 1)]

        if (currentContact.value?.id === contactId) {
            currentContact.value = {
                ...currentContact.value,
                name: next.name,
            }
        }
    }

    return {
        contacts,
        pagination,
        currentContact,
        loading,
        lastFetchError,
        contactDetailAddresses,
        contactDetailStatus,
        fetchContacts,
        suggestContacts,
        searchContacts,
        clearSessionContacts,
        create,
        createWithAddress,
        getById,
        ensureContactDetailLoaded,
        setContactDetailAddresses,
        setDefaultAddress,
        update,
        remove,
        refreshContact,
        clearCurrentContact,
        submitEditContact,
    }
})
